/**
 * Top-right Open / Closed lifecycle filter.
 *
 * Joined segmented pill, two halves. Each segment is a clickable
 * trigger that anchors its own popover of sub-status options scoped
 * to that side:
 *
 *   [ Open  649 v | Closed  12 v ]
 *
 * Click the Open half - popover shows "Needs review", "Blocked",
 * "All open". Click Closed - popover shows "Resolved", "Ignored",
 * "All closed". Picking a sub-filter shows it inside the segment:
 *
 *   [ Open - Blocked  1 v | Closed  12 v ]
 *
 * The amber palette stays on the active Open segment; the active
 * Closed segment uses neutral slate. Blocked sub-filter carries the
 * existing BlockIcon (red) inline as a risk cue.
 *
 * Zero-count sub-status rows hide unless they're the currently-selected
 * value (so a deep-link to ?status=blocked still renders the option
 * highlighted even if the bucket is empty by the time the user lands).
 *
 * Data-driven: renders only when the envelope exposes both
 * `open_total` and `fixed_total_30d`. Routes that opt out of lifecycle
 * filtering simply omit those keys and the whole control hides.
 *
 * URL behaviour preserved across the legacy alias matrix:
 *   ?status=open                     -> Open parent, All sub-status
 *   ?status=closed                   -> Closed parent, All sub-status
 *   ?status=needs_review (or hyphen) -> Open parent, Needs review
 *   ?status=blocked                  -> Open parent, Blocked
 *   ?status=resolved | fixed         -> Closed parent, Resolved
 *   ?status=ignored                  -> Closed parent, Ignored
 *   ?status=active                   -> Open parent (legacy fallback)
 */
import React, { useState } from 'react'
import {
  Box,
  Divider,
  MenuItem,
  MenuList,
  Popover,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import { URLSearchParamsInit } from 'react-router-dom'

import {
  ExceptionStatus,
  ExceptionStatusValue,
  readExceptionStatus,
  readExceptionStatusValue,
} from './exceptionStatus'
import { useAuth } from '../../../contexts/AuthContext'
import { canBlockExceptions } from '../../../services/auth'
import {
  ChipState,
  iconFor,
  STATUS_LABEL,
} from '../../../config/exceptions/shared/issuesState'

interface ExceptionStatusToggleProps {
  response: Record<string, any> | null
  searchParams: URLSearchParams
  setSearchParams: (
    next: URLSearchParamsInit,
    options?: { replace?: boolean },
  ) => void
}

type SegmentPalette = {
  bg: string
  border: string
  text: string
  countBg: string
  countText: string
}

// Warm amber: attention/needs-action read. Matches the prior two-segment
// pill exactly so existing screenshots/docs still line up.
const OPEN_SELECTED: SegmentPalette = {
  bg: '#FFF7ED',
  border: '#FDBA74',
  text: '#C2410C',
  countBg: '#FFEDD5',
  countText: '#C2410C',
}

// Neutral slate: "settled" read without any teal tint, so the Closed
// segment reads as a peer of the page-size and pagination chrome.
const CLOSED_SELECTED: SegmentPalette = {
  bg: '#F1F5F9',
  border: '#CBD5E1',
  text: '#475569',
  countBg: '#E2E8F0',
  countText: '#334155',
}

const UNSELECTED: SegmentPalette = {
  bg: '#FFFFFF',
  border: '#DADDE1',
  text: '#5F6670',
  countBg: '#F1F3F5',
  countText: '#5F6670',
}

const buildSegmentSx = (selected: SegmentPalette) => ({
  textTransform: 'none' as const,
  px: 1.75,
  py: 0.5,
  fontSize: '0.8125rem',
  fontWeight: 500,
  bgcolor: UNSELECTED.bg,
  color: UNSELECTED.text,
  border: `1px solid ${UNSELECTED.border}`,
  transition:
    'background-color 120ms ease, border-color 120ms ease, color 120ms ease',
  '&:hover': {
    bgcolor: 'rgba(0, 0, 0, 0.02)',
    borderColor: UNSELECTED.border,
  },
  '&.Mui-selected': {
    bgcolor: selected.bg,
    color: selected.text,
    borderColor: selected.border,
    fontWeight: 600,
    zIndex: 1,
    '&:hover': {
      bgcolor: selected.bg,
      borderColor: selected.border,
    },
  },
  '&.Mui-focusVisible': {
    outline: `2px solid ${selected.border}`,
    outlineOffset: 2,
  },
})

const Count: React.FC<{
  value: number
  palette: SegmentPalette
}> = ({ value, palette }) => (
  <Typography
    component="span"
    sx={{
      ml: 0.75,
      px: 0.5,
      py: 0,
      borderRadius: 0.75,
      fontSize: '0.75rem',
      fontWeight: 700,
      fontVariantNumeric: 'tabular-nums',
      bgcolor: palette.countBg,
      color: palette.countText,
      lineHeight: 1.6,
    }}
  >
    {value.toLocaleString()}
  </Typography>
)

type SubStatusOption = {
  value: ExceptionStatusValue
  label: string
  count: number
  parent: ExceptionStatus
  isAll?: boolean
}

// Sub-status URL values map 1:1 to the ChipState used by iconFor()
// except the 'All' rows (parent literals) which deliberately render
// no icon. Returning null means "no icon".
const subValueToChipState = (
  value: ExceptionStatusValue,
): ChipState | null => {
  if (value === 'needs_review') return 'needs_review'
  if (value === 'blocked') return 'blocked'
  if (value === 'resolved') return 'resolved'
  if (value === 'ignored') return 'ignored'
  return null
}

// Tints applied to the iconFor() output inside a popover row. Matches
// the per-state palette tokens from issuesState.ts so the row icon
// reads the same as the IssuesChip elsewhere in the app.
const subIconColor = (state: ChipState): string => {
  if (state === 'needs_review') return '#ED6C02'
  if (state === 'blocked') return '#D32F2F'
  if (state === 'resolved') return '#007F98'
  if (state === 'ignored') return '#6B7280'
  return 'inherit'
}

const ExceptionStatusToggle: React.FC<ExceptionStatusToggleProps> = ({
  response,
  searchParams,
  setSearchParams,
}) => {
  // Blocked / Ignored buckets stay role-gated: EVO_ADMIN / EVO_TEAM /
  // PSC_TEAM only. Token-only inspector loads return user=undefined,
  // which the helper treats as "no" - external FP-token views hide
  // the Blocked row but still see Needs review / All open.
  const { user: authUser } = useAuth()
  const showBlockedRow = canBlockExceptions(authUser)

  const [openAnchor, setOpenAnchor] = useState<HTMLElement | null>(null)
  const [closedAnchor, setClosedAnchor] = useState<HTMLElement | null>(null)

  const openCount =
    typeof response?.open_total === 'number'
      ? (response.open_total as number)
      : null
  const resolvedCount =
    typeof response?.fixed_total_30d === 'number'
      ? (response.fixed_total_30d as number)
      : null
  const ignoredCount =
    typeof response?.ignored_total === 'number'
      ? (response.ignored_total as number)
      : 0
  const needsReviewCount =
    typeof response?.needs_review_total === 'number'
      ? (response.needs_review_total as number)
      : null
  const blockedCount =
    typeof response?.blocked_total === 'number'
      ? (response.blocked_total as number)
      : null

  // Data-presence guard - opt-out routes (e.g. systems detector) omit
  // these keys, and the toggle hides accordingly.
  if (openCount === null || resolvedCount === null) return null

  const closedCount = resolvedCount + ignoredCount
  const currentStatus = readExceptionStatus(searchParams)
  const currentValue = readExceptionStatusValue(searchParams)

  const openSubOptions: SubStatusOption[] = []
  if (needsReviewCount !== null) {
    openSubOptions.push({
      value: 'needs_review',
      label: STATUS_LABEL.needs_review,
      count: needsReviewCount,
      parent: 'open',
    })
  }
  if (blockedCount !== null && showBlockedRow) {
    openSubOptions.push({
      value: 'blocked',
      label: STATUS_LABEL.blocked,
      count: blockedCount,
      parent: 'open',
    })
  }
  openSubOptions.push({
    value: 'open',
    label: 'All open',
    count: openCount,
    parent: 'open',
    isAll: true,
  })

  const closedSubOptions: SubStatusOption[] = [
    {
      value: 'resolved',
      label: STATUS_LABEL.resolved,
      count: resolvedCount,
      parent: 'closed',
    },
    {
      value: 'ignored',
      label: STATUS_LABEL.ignored,
      count: ignoredCount,
      parent: 'closed',
    },
    {
      value: 'closed',
      label: 'All closed',
      count: closedCount,
      parent: 'closed',
      isAll: true,
    },
  ]

  // Hide zero-count rows unless they're the currently-selected value -
  // a deep-link to ?status=blocked on an empty bucket should still
  // render the option highlighted (otherwise the popover would silently
  // omit it).
  const filterZeros = (opts: SubStatusOption[]) =>
    opts.filter(
      (opt) => opt.isAll || opt.count > 0 || currentValue === opt.value,
    )

  const visibleOpenOptions = filterZeros(openSubOptions)
  const visibleClosedOptions = filterZeros(closedSubOptions)

  const activeOpenSub =
    currentStatus === 'open'
      ? visibleOpenOptions.find(
          (o) => o.value === currentValue && !o.isAll,
        ) ?? null
      : null
  const activeClosedSub =
    currentStatus === 'closed'
      ? visibleClosedOptions.find(
          (o) => o.value === currentValue && !o.isAll,
        ) ?? null
      : null

  const writeStatus = (next: ExceptionStatusValue) => {
    const params = new URLSearchParams(searchParams)
    if (next === 'open') params.delete('status')
    else params.set('status', next)
    // Status swaps the whole result set, so a stale offset would strand
    // the user on an out-of-range page. Reset to page 1 like every other
    // URL-write site (see FilterStripRenderer page-reset convention).
    params.delete('page')
    setSearchParams(params)
  }

  const handleSegmentClick = (
    parent: ExceptionStatus,
    anchorEl: HTMLElement,
  ) => {
    if (parent === 'open') setOpenAnchor(anchorEl)
    else setClosedAnchor(anchorEl)
  }

  const handleSubSelect = (next: ExceptionStatusValue) => {
    writeStatus(next)
    setOpenAnchor(null)
    setClosedAnchor(null)
  }

  const renderPopoverRow = (opt: SubStatusOption) => {
    const isCurrent = opt.value === currentValue
    const chipState = subValueToChipState(opt.value)
    const palette = opt.parent === 'open' ? OPEN_SELECTED : CLOSED_SELECTED
    const icon = chipState ? iconFor(chipState) : null
    return (
      <MenuItem
        key={opt.value}
        dense
        selected={isCurrent}
        onClick={() => handleSubSelect(opt.value)}
        sx={{
          fontSize: '0.8125rem',
          minWidth: 220,
          py: 0.75,
          gap: 1,
          '&.Mui-selected': {
            bgcolor: palette.bg,
            '&:hover': { bgcolor: palette.bg },
          },
        }}
      >
        <Box
          component="span"
          sx={{
            width: 18,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: chipState ? subIconColor(chipState) : 'transparent',
            '& svg': { fontSize: 18 },
          }}
        >
          {icon}
        </Box>
        <Box component="span" sx={{ flexGrow: 1 }}>
          {opt.label}
        </Box>
        <Typography
          component="span"
          sx={{
            fontSize: '0.75rem',
            fontWeight: 600,
            fontVariantNumeric: 'tabular-nums',
            color: 'text.secondary',
            ml: 2,
          }}
        >
          {opt.count.toLocaleString()}
        </Typography>
      </MenuItem>
    )
  }

  const renderPopoverList = (opts: SubStatusOption[]) => {
    const nonAll = opts.filter((o) => !o.isAll)
    const all = opts.filter((o) => o.isAll)
    return (
      <MenuList dense sx={{ py: 0.5 }}>
        {nonAll.map(renderPopoverRow)}
        {nonAll.length > 0 && all.length > 0 && (
          <Divider sx={{ my: 0.5 }} />
        )}
        {all.map(renderPopoverRow)}
      </MenuList>
    )
  }

  const renderSegmentInner = (
    label: string,
    isActive: boolean,
    parentCount: number,
    palette: SegmentPalette,
    activeSub: SubStatusOption | null,
  ) => {
    const showSubBadge = isActive && !!activeSub
    const subChipState = activeSub
      ? subValueToChipState(activeSub.value)
      : null
    const countPalette = isActive ? palette : UNSELECTED
    const caretColor = isActive ? palette.text : UNSELECTED.text
    return (
      <Box
        component="span"
        sx={{ display: 'inline-flex', alignItems: 'center' }}
      >
        {label}
        {showSubBadge && activeSub && (
          <>
            <Box
              component="span"
              aria-hidden="true"
              sx={{
                mx: 0.625,
                color: palette.text,
                opacity: 0.45,
                fontWeight: 400,
                lineHeight: 1,
              }}
            >
              ·
            </Box>
            {subChipState && (
              <Box
                component="span"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  mr: 0.375,
                  color: subIconColor(subChipState),
                  '& svg': { fontSize: 15 },
                }}
              >
                {iconFor(subChipState)}
              </Box>
            )}
            <Box component="span" sx={{ fontWeight: 400 }}>
              {activeSub.label}
            </Box>
          </>
        )}
        <Count
          value={showSubBadge && activeSub ? activeSub.count : parentCount}
          palette={countPalette}
        />
        <ArrowDropDownIcon
          sx={{ ml: 0.25, fontSize: 18, color: caretColor }}
        />
      </Box>
    )
  }

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        flexWrap: 'nowrap',
      }}
    >
      <ToggleButtonGroup
        value={currentStatus}
        exclusive
        size="small"
        aria-label="Exception status"
        sx={{
          bgcolor: 'background.paper',
          '& .MuiToggleButtonGroup-grouped': {
            borderRadius: 1.5,
            '&:not(:first-of-type)': {
              borderLeft: `1px solid ${UNSELECTED.border}`,
              marginLeft: '-1px',
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
            },
            '&:not(:last-of-type)': {
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
            },
          },
        }}
      >
        <ToggleButton
          value="open"
          selected={currentStatus === 'open'}
          onClick={(e) => handleSegmentClick('open', e.currentTarget)}
          aria-label="Open exceptions"
          aria-haspopup="menu"
          aria-expanded={!!openAnchor}
          sx={buildSegmentSx(OPEN_SELECTED)}
        >
          {renderSegmentInner(
            'Open',
            currentStatus === 'open',
            openCount,
            OPEN_SELECTED,
            activeOpenSub,
          )}
        </ToggleButton>
        <ToggleButton
          value="closed"
          selected={currentStatus === 'closed'}
          onClick={(e) => handleSegmentClick('closed', e.currentTarget)}
          aria-label="Closed exceptions (resolved or ignored)"
          aria-haspopup="menu"
          aria-expanded={!!closedAnchor}
          sx={buildSegmentSx(CLOSED_SELECTED)}
        >
          {renderSegmentInner(
            'Closed',
            currentStatus === 'closed',
            closedCount,
            CLOSED_SELECTED,
            activeClosedSub,
          )}
        </ToggleButton>
      </ToggleButtonGroup>
      <Popover
        open={!!openAnchor}
        anchorEl={openAnchor}
        onClose={() => setOpenAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { mt: 0.5, minWidth: 220 } } }}
      >
        {renderPopoverList(visibleOpenOptions)}
      </Popover>
      <Popover
        open={!!closedAnchor}
        anchorEl={closedAnchor}
        onClose={() => setClosedAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { mt: 0.5, minWidth: 220 } } }}
      >
        {renderPopoverList(visibleClosedOptions)}
      </Popover>
      {currentStatus === 'closed' && (
        <Typography
          component="span"
          sx={{
            fontSize: '0.75rem',
            color: 'text.secondary',
            whiteSpace: 'nowrap',
          }}
        >
          last 30 days
        </Typography>
      )}
    </Box>
  )
}

export default ExceptionStatusToggle
